import { type Option } from '@avanta/shared/components/ui/Select';
import {
  AppointmentDuration,
  AppointmentType,
  FLOW_MODES,
} from '@avanta/shared/constants/appointments';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import { type Treatment } from '@avanta/shared/types/appointments';
import { type DefaultValues, type UseFormSetValue } from 'react-hook-form';
import {
  type AppointmentPayload,
  type EditAppointmentPayload,
  type Flow,
  type Speciality,
} from 'types/appointments';
import { type ContractRecordField } from 'types/enterprises';
import { type PatientRecord } from 'types/patient';
import { AppointmentsReservePayload, FLOW_STEP_NAMES } from './constants';
import {
  type AppointmentsReserveFormData,
  type AppointmentsReserveLocationState,
  type ReserveFlow,
  type Steps,
} from './types';

type RecordFieldValue = AppointmentsReserveFormData['recordFields'][string];

interface GetDefaultValuesParams {
  state: AppointmentsReserveLocationState;
  patientRecord?: PatientRecord;
}

export const getFlowStepNames = (flow: Flow): readonly Steps[] =>
  FLOW_STEP_NAMES[flow as ReserveFlow];

export const getInitialStepName = (rawStep: number | Steps | undefined, flow: Flow): Steps => {
  const stepNames = getFlowStepNames(flow);

  if (typeof rawStep === 'string') {
    const matchingStepName = stepNames.find(stepName => stepName === rawStep);
    if (matchingStepName) return matchingStepName;
  }
  return stepNames[Number(rawStep)] ?? stepNames[0];
};

/** Moves the wizard to the step that follows a flow switch, keeping flow and step in sync. */
export const changeFlow = (
  setValue: UseFormSetValue<AppointmentsReserveFormData>,
  previousFlow: Flow,
  nextFlow: Flow
): void => {
  const involvesFacultativeSearch =
    nextFlow === FLOW_MODES.search_facultative || previousFlow === FLOW_MODES.search_facultative;
  const stepNames = getFlowStepNames(nextFlow);

  setValue(AppointmentsReservePayload.flow, nextFlow);
  setValue(
    AppointmentsReservePayload.currentStepName,
    stepNames[involvesFacultativeSearch ? 1 : 0]
  );
};

/** Flows where the professional is already chosen and must not be cleared automatically. */
export const hasFixedPsychologist = (flow: Flow): boolean =>
  flow === FLOW_MODES.edit || flow === FLOW_MODES.search_facultative;

export const resetAvailabilitySelection = (
  setValue: UseFormSetValue<AppointmentsReserveFormData>,
  flow: Flow
): void => {
  setValue(AppointmentsReservePayload.dateTime, '');
  if (!hasFixedPsychologist(flow)) {
    setValue(AppointmentsReservePayload.psychologist, undefined);
  }
};

export const resetContractSelection = (
  setValue: UseFormSetValue<AppointmentsReserveFormData>,
  flow: Flow,
  contractId = ''
): void => {
  resetAvailabilitySelection(setValue, flow);
  setValue(AppointmentsReservePayload.contractRateId, '');
  setValue(AppointmentsReservePayload.recordFields, {});
  setValue(AppointmentsReservePayload.contractId, contractId);
};

export const setSelectedCenter = (
  setValue: UseFormSetValue<AppointmentsReserveFormData>,
  centerId: string,
  city: string
): void => {
  setValue(AppointmentsReservePayload.backofficeCenterId, centerId);
  setValue(AppointmentsReservePayload.city, city);
};

const isRecordFieldValue = (value: unknown): value is RecordFieldValue =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

export const hasRecordFieldValue = (value: RecordFieldValue | undefined): boolean => {
  if (typeof value === 'boolean') return true;
  if (typeof value === 'number') return !Number.isNaN(value);
  if (typeof value === 'string') return value.trim() !== '';

  return false;
};

export const areRecordFieldsComplete = (
  recordFields: ContractRecordField[],
  values: Record<string, RecordFieldValue>
): boolean =>
  recordFields
    .filter(recordField => recordField.active && recordField.required)
    .every(recordField => hasRecordFieldValue(values[recordField.key]));

export const getRecordFieldValues = (
  values?: Record<string, unknown>
): Record<string, RecordFieldValue> => {
  if (!values) return {};

  return Object.entries(values).reduce<Record<string, RecordFieldValue>>(
    (recordFieldValues, [key, value]) => {
      if (isRecordFieldValue(value)) {
        recordFieldValues[key] = value;
      }

      return recordFieldValues;
    },
    {}
  );
};

export const mapSpecialities = (specialities: Speciality[]): Option[] =>
  specialities.map(speciality => ({
    label: t(`AppointmentsReserve.Steps.speciality.options.${speciality.name}`, {
      ns: LOCIZE_NS.APPOINTMENTS,
    }),
    value: speciality.id,
  }));

export const mapTreatments = (
  specialityId: number | undefined,
  treatments: Treatment[]
): Option[] =>
  treatments
    .filter(treatment => treatment.speciality.id === Number(specialityId))
    .map(treatment => ({
      label: t(`AppointmentsReserve.Steps.treatment.options.${treatment.id}`, {
        ns: LOCIZE_NS.APPOINTMENTS,
      }),
      value: treatment.id,
    }));

const getAppointmentDefaults = ({
  appointment,
  psychologist,
}: Pick<
  AppointmentsReserveLocationState,
  'appointment' | 'psychologist'
>): DefaultValues<AppointmentsReserveFormData> =>
  appointment
    ? {
        city: appointment.center?.city ?? '',
        backofficeCenterId: String(appointment.center?.id ?? ''),
        dateTime: appointment.dateTime,
        specialityBooked: appointment.specialitiesBookable,
        psychologistServiceBooked: appointment.psychologistServiceBooked,
        observations: appointment.observations ?? '',
        contractAuthorization: appointment.contractAuthorization ?? '',
        psychologist,
        durationType: appointment.durationInMinutes,
      }
    : {};

const getContractDefaults = (
  patientRecord?: PatientRecord
): DefaultValues<AppointmentsReserveFormData> => ({
  contractId: patientRecord?.contract ? String(patientRecord.contract.id) : '',
  contractRateId: patientRecord?.selectedRate ? String(patientRecord.selectedRate) : '',
  recordFields: getRecordFieldValues(patientRecord?.values),
  hasExpedient: Boolean(patientRecord?.code),
});

export const getDefaultValues = ({
  state,
  patientRecord,
}: GetDefaultValuesParams): DefaultValues<AppointmentsReserveFormData> => {
  const flow = state.flow ?? FLOW_MODES.regular;

  return {
    flow,
    currentStepName: getInitialStepName(state.step, flow),
    ...getContractDefaults(patientRecord),
    patient: state.patient,
    ...getAppointmentDefaults(state),
  };
};

export const buildBookPayload = (data: AppointmentsReserveFormData): AppointmentPayload => ({
  psychologistId: data.psychologist?.id ?? '',
  patientId: data.patient?.id ?? '',
  dateTime: data.dateTime,
  contactMethod: AppointmentType.InPerson,
  durationType: AppointmentDuration.DURATION_DOUBLE,
  backofficeCenterId: Number(data.backofficeCenterId),
  specialityBooked: Number(data.specialityBooked),
  psychologistServiceBooked: Number(data.psychologistServiceBooked),
  observations: data.observations ?? '',
  contractId: Number(data.contractId),
  contractRateId: Number(data.contractRateId),
  contractAuthorization: data.contractAuthorization,
  recordFields: data.recordFields,
});

export const buildEditPayload = (
  data: AppointmentsReserveFormData,
  previousDate: string
): EditAppointmentPayload => ({
  patientId: data.patient?.id ?? '',
  psychologistId: data.psychologist?.id ?? '',
  previousDate,
  newDate: data.dateTime,
  reason: data.observations,
});
